export function createDefaultWorkDetail(department, percentage = '100') {
  return {
    deptName: department,
    percentage
  };
}

export function parseWorkDetails(workDetails) {
  if (typeof workDetails === 'string') {
    try {
      return JSON.parse(workDetails);
    } catch (e) {
      return [];
    }
  }
  return Array.isArray(workDetails) ? workDetails : [];
}

export function normalizeWorkDetails(workDetails, department) {
  const details = parseWorkDetails(workDetails)
    .filter(item => item && typeof item === 'object')
    .map(item => ({
      deptName: item.deptName || department,
      percentage: item.percentage === null || item.percentage === undefined || item.percentage === '' ? null : String(item.percentage)
    }));

  return details.length ? details : [createDefaultWorkDetail(department)];
}

export function ensureWorkDetails(row, department, setter) {
  const details = normalizeWorkDetails(row.workDetails, department);
  if (setter) {
    setter(row, 'workDetails', details);
  } else {
    row.workDetails = details;
  }
  return row.workDetails;
}

export function addWorkDetail(row, department, setter) {
  const details = parseWorkDetails(row.workDetails);
  const nextDetails = details.length
    ? [...details, createDefaultWorkDetail(department, 0)]
    : [createDefaultWorkDetail(department)];

  if (setter) {
    setter(row, 'workDetails', nextDetails);
  } else {
    row.workDetails = nextDetails;
  }
  return row.workDetails;
}

export function deleteWorkDetail(row, index, department, setter) {
  const details = parseWorkDetails(row.workDetails);
  const nextDetails = details.filter((item, detailIndex) => detailIndex !== index);
  const shouldCollapse = nextDetails.length === 0;
  const finalDetails = shouldCollapse ? [createDefaultWorkDetail(department)] : nextDetails;

  if (setter) {
    setter(row, 'workDetails', finalDetails);
  } else {
    row.workDetails = finalDetails;
  }

  return {
    shouldCollapse,
    workDetails: row.workDetails
  };
}

export function toggleExpandedRow(expandedRows, row) {
  const isExpanded = expandedRows.includes(row);
  return {
    expandedRows: isExpanded ? expandedRows.filter(item => item !== row) : [...expandedRows, row],
    expanded: !isExpanded
  };
}

export function isIntegerPercentage(value) {
  if (value === null || value === undefined || value === '') return false;
  return /^(0|[1-9]\d*)$/.test(String(value)) && Number(value) <= 100;
}

export function getWorkDetailsError(workDetails) {
  const details = parseWorkDetails(workDetails);
  if (!details.length) return 'percentage';

  const deptNames = new Set();
  let total = 0;

  for (const item of details) {
    const deptName = item && item.deptName !== null && item.deptName !== undefined ? String(item.deptName).trim() : '';
    if (!deptName) return 'data';
    if (deptNames.has(deptName)) return 'duplicateDept';
    deptNames.add(deptName);

    if (!isIntegerPercentage(item.percentage)) return 'percentage';
    total += Number(item.percentage);
  }

  return total === 100 ? null : 'percentage';
}

export function isWorkDetailsValid(workDetails) {
  return !getWorkDetailsError(workDetails);
}

export function formatWorkDetailsMessage(template, moduleName, rowNumber) {
  return template
    .replace('{module}', moduleName)
    .replace('{row}', rowNumber);
}

export function validateWorkDetailsList(list, moduleName, reportError, messages = {}) {
  const invalidIndex = list.findIndex(item => getWorkDetailsError(item.workDetails));
  if (invalidIndex === -1) {
    return true;
  }

  const errorType = getWorkDetailsError(list[invalidIndex].workDetails);
  const rowNumber = invalidIndex + 1;
  if (errorType === 'duplicateDept') {
    reportError(formatWorkDetailsMessage(messages.duplicateDept || '{module}第{row}行部门有重复值', moduleName, rowNumber));
    return false;
  }
  if (errorType === 'percentage') {
    reportError(formatWorkDetailsMessage(messages.percentageNotEqual100 || '{module}第{row}行百分比不等于100', moduleName, rowNumber));
    return false;
  }

  reportError(formatWorkDetailsMessage(messages.dataError || '{module}第{row}行数据错误', moduleName, rowNumber));
  return false;
}

export function normalizeWorkDetailsForSubmit(list, department) {
  return list.map(item => ({
    ...item,
    workDetails: normalizeWorkDetails(item.workDetails, department).map(detail => ({
      deptName: detail.deptName || department,
      percentage: String(detail.percentage)
    }))
  }));
}

export function getActualStaffKey(item) {
  if (!item) return '';
  const key = item.staffId || item.id || item.staffCode || item.staffNo || item.code || item.name || item.staffName;
  return key === null || key === undefined ? '' : String(key).trim();
}

export function createActualStaffRow(item, defaultHours, department, hourField = 'staffListHours') {
  return {
    ...item,
    name: item.name || item.staffName,
    [hourField]: defaultHours,
    workDetails: normalizeWorkDetails(null, department)
  };
}

export function mergeActualStaffList(currentList = [], selectedList = [], defaultHours = [], department, hourField = 'staffListHours') {
  const usedKeys = new Set();
  const result = [];

  currentList.forEach(item => {
    const key = getActualStaffKey(item);
    if (!key || usedKeys.has(key)) return;
    usedKeys.add(key);
    result.push(item);
  });

  selectedList.forEach(item => {
    const key = getActualStaffKey(item);
    if (!key || usedKeys.has(key)) return;
    usedKeys.add(key);
    result.push(createActualStaffRow(item, defaultHours, department, hourField));
  });

  return result;
}

export function createOvertimeStaffRow(item, defaultHours) {
  return {
    ...item,
    name: item.name || item.staffName,
    staffListHours: defaultHours
  };
}

export function mergeOvertimeStaffList(currentList = [], selectedList = [], defaultHours = []) {
  const usedKeys = new Set();
  const result = [];

  currentList.forEach(item => {
    const key = getActualStaffKey(item);
    if (!key || usedKeys.has(key)) return;
    usedKeys.add(key);
    result.push(item);
  });

  selectedList.forEach(item => {
    const key = getActualStaffKey(item);
    if (!key || usedKeys.has(key)) return;
    usedKeys.add(key);
    result.push(createOvertimeStaffRow(item, defaultHours));
  });

  return result;
}
